import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import * as styles from './RightPanel.module.scss';
import IconButton from '../../components/IconButton';
import StickyHeader from '../../components/StickyHeader';
import closeIcon from '../../icons/non-prefilled/16x16/clear.svg';
import useIsLayoutSize from '../../post/utils/useIsLayoutSize';
import { LayoutSize } from '../../post/utils/useLayoutSize';
import addTestId from '../../utils/addTestId';

const ANIMATION_DURATION_MS = parseInt(styles.layoutAnimationDurationMs, 10);
const RIGHT_PANEL_WIDTH = parseInt(styles.rightPanelWidth, 10);
const RIGHT_PANEL_MOBILE_WIDTH = parseInt(styles.rightPanelMobileWidth, 10);

const RightPanelContext = createContext<{
  rightPanel: ReactNode | null;
  setRightPanel: Dispatch<SetStateAction<ReactNode>>;
}>({
  rightPanel: null,
  setRightPanel: () => {},
});

interface RightPanelProviderProps {
  children: React.ReactNode;

  /**
   * Used for Storybook only
   */
  initialRightPanel?: ReactNode;
}

const RightPanelProvider = ({
  children,
  initialRightPanel,
}: RightPanelProviderProps) => {
  const [rightPanel, setRightPanel] = useState(initialRightPanel);

  const value = useMemo(
    () => ({ rightPanel, setRightPanel }),
    [rightPanel, setRightPanel],
  );

  return (
    <RightPanelContext.Provider value={value}>
      {children}
    </RightPanelContext.Provider>
  );
};

export const useRightPanel = () => {
  const { rightPanel, setRightPanel } = useContext(RightPanelContext);

  const isRightPanelOpen = !!rightPanel;

  const openRightPanel: (node: JSX.Element) => void = setRightPanel;
  const closeRightPanel = useCallback(() => {
    setRightPanel(null);
  }, [setRightPanel]);

  return { isRightPanelOpen, openRightPanel, closeRightPanel };
};

export const useRightPanelWidth = () => {
  const { rightPanel } = useContext(RightPanelContext);
  const isMobile = useIsLayoutSize(LayoutSize.mobile);

  if (!rightPanel) {
    return 0;
  }

  return isMobile ? RIGHT_PANEL_MOBILE_WIDTH : RIGHT_PANEL_WIDTH;
};

const RightPanelRoot = () => {
  const { rightPanel } = useContext(RightPanelContext);

  return (
    <div className={styles.rightPanelRootContainer}>
      <div
        data-no-print
        className={classNames(styles.rightPanelRoot, {
          [styles.isOpen]: rightPanel,
        })}
      >
        {/* Keep the right panel content visible during close animation */}
        <AnimatePresence>
          {!!rightPanel && (
            <motion.div
              key="view-container"
              exit={{ display: 'none' }}
              transition={{ duration: ANIMATION_DURATION_MS / 1000 }}
              className={styles.rightPanelRootContent}
            >
              {rightPanel}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

interface RightPanelInnerProps {
  header?: string;
  children?: JSX.Element[] | JSX.Element;
  footer?: JSX.Element[] | JSX.Element;
}

const RightPanelInner = ({
  header,
  children,
  footer,
}: RightPanelInnerProps) => {
  const { closeRightPanel } = useRightPanel();

  return (
    <div
      className={styles.rightPanelInnerContainer}
      {...addTestId('rightPanel')}
    >
      <div className={styles.rightPanelInner}>
        <StickyHeader>
          <div className={styles.rightPanelHeader}>
            <h3>{header}</h3>
            <IconButton
              size="large"
              onClick={closeRightPanel}
              applyNegativeMargin
              aria-label="Close right panel"
            >
              {closeIcon}
            </IconButton>
          </div>
        </StickyHeader>
        <div className={styles.rightPanelInnerContent}>{children}</div>
      </div>
      {footer && <div className={styles.rightPanelFooter}>{footer}</div>}
    </div>
  );
};

export default {
  Provider: RightPanelProvider,
  Root: RightPanelRoot,
  Inner: RightPanelInner,
};
